import { useEffect, useState } from 'react';

/* ─── Animated count-up (eases from 0 to target) ───────────────────── */
const useCountUp = (target, durationMs = 1800) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (target == null || isNaN(target)) return;
    let raf;
    const start = performance.now();
    const from = 0;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return val;
};

/* ─── Mini constellation tuned for compact hero card ─────────────── */
const MiniConstellation = () => {
  const NODES = [
    { x: 60,  y: 50 },  { x: 180, y: 30 }, { x: 320, y: 70 },
    { x: 460, y: 40 },  { x: 580, y: 90 }, { x: 120, y: 140 },
    { x: 260, y: 170 }, { x: 400, y: 150 }, { x: 540, y: 180 },
  ];
  const EDGES = [[0,1],[1,2],[2,3],[3,4],[0,5],[1,6],[2,6],[3,7],[4,8],[5,6],[6,7],[7,8]];
  return (
    <svg viewBox="0 0 640 220" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <radialGradient id="hero-node-glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%"  stopColor="#bfdbfe" stopOpacity="0.9" />
          <stop offset="60%" stopColor="#60a5fa" stopOpacity="0.3" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
        </radialGradient>
      </defs>
      {EDGES.map(([a, b], i) => {
        const A = NODES[a], B = NODES[b];
        return (
          <line
            key={i}
            x1={A.x} y1={A.y} x2={B.x} y2={B.y}
            stroke="#bfdbfe"
            strokeWidth="0.6"
            className="animate-pulse-line"
            style={{ animationDelay: `${(i * 0.3) % 3.5}s` }}
          />
        );
      })}
      {EDGES.filter((_, i) => i % 3 === 0).map(([a, b], i) => {
        const A = NODES[a], B = NODES[b];
        return (
          <circle key={`p-${i}`} r="1.6" fill="#dbeafe">
            <animate attributeName="cx" from={A.x} to={B.x} dur={`${3 + (i % 2)}s`} begin={`${i * 0.7}s`} repeatCount="indefinite" />
            <animate attributeName="cy" from={A.y} to={B.y} dur={`${3 + (i % 2)}s`} begin={`${i * 0.7}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0;1;1;0" dur={`${3 + (i % 2)}s`} begin={`${i * 0.7}s`} repeatCount="indefinite" />
          </circle>
        );
      })}
      {NODES.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r="12" fill="url(#hero-node-glow)" opacity="0.6" />
          <circle cx={n.x} cy={n.y} r="2" fill="#ffffff" />
        </g>
      ))}
    </svg>
  );
};

/* ─── Live mini-metric pill (with pulse dot + animated value) ────── */
const LiveMetric = ({ label, target, suffix = '', pulseColor = 'bg-emerald-400' }) => {
  const val = useCountUp(target ?? 0);
  return (
    <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-3 ring-1 ring-white/20 min-w-[120px]">
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`w-1.5 h-1.5 rounded-full ${pulseColor} animate-pulse`} />
        <span className="text-[10px] uppercase tracking-widest text-white/60 font-bold">{label}</span>
      </div>
      <p className="text-2xl font-bold text-white tabular-nums leading-none">
        {val}<span className="text-sm text-white/60 font-semibold">{suffix}</span>
      </p>
    </div>
  );
};

/* ─── The contained cinematic dashboard hero ─────────────────────── */
const DashboardHero = ({ greeting, name, subtitle, stats }) => {
  const passRate =
    stats?.totalAttempts > 0
      ? Math.round(((stats.totalPassed || 0) / stats.totalAttempts) * 100)
      : 0;

  return (
    <div className="relative overflow-hidden rounded-3xl bg-auth-gradient mb-8 shadow-card-lg">
      {/* Aurora mesh (contained) */}
      <div className="absolute top-[-30%] left-[10%] w-[420px] h-[420px] rounded-full bg-sky-400/40 blur-[100px] animate-aurora-1 mix-blend-screen pointer-events-none" />
      <div className="absolute top-[20%] right-[-10%] w-[360px] h-[360px] rounded-full bg-indigo-500/40 blur-[100px] animate-aurora-2 mix-blend-screen pointer-events-none" />
      <div className="absolute bottom-[-40%] left-[40%] w-[420px] h-[420px] rounded-full bg-cyan-300/30 blur-[110px] animate-aurora-3 mix-blend-screen pointer-events-none" />

      {/* Constellation network */}
      <div className="absolute inset-0 opacity-60">
        <MiniConstellation />
      </div>

      {/* Drifting particles (subtle, fewer than auth) */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 10 }).map((_, i) => (
          <span
            key={i}
            className="absolute bottom-0 rounded-full bg-white animate-drift-up"
            style={{
              left: `${(i * 67) % 100}%`,
              width: `${1 + (i % 2)}px`,
              height: `${1 + (i % 2)}px`,
              animationDelay: `${(i * 1.1) % 12}s`,
              animationDuration: `${10 + (i % 4)}s`,
              opacity: 0.4,
            }}
          />
        ))}
      </div>

      {/* Pulse ring (focal point) */}
      <div className="absolute top-1/2 right-12 -translate-y-1/2 w-3 h-3 pointer-events-none hidden lg:block">
        <span className="absolute inset-0 rounded-full bg-sky-200" />
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="absolute inset-0 rounded-full border border-sky-200/60 animate-ping-ring"
            style={{ animationDelay: `${i * 1.05}s` }}
          />
        ))}
      </div>

      {/* Foreground content */}
      <div className="relative z-10 px-8 py-8 lg:px-10 lg:py-9 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        <div className="max-w-xl">
          <p className="text-primary-200 text-sm font-medium tracking-wide">{greeting}</p>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight mt-1 text-white leading-tight">
            <span className="bg-gradient-to-r from-white via-sky-100 to-white bg-clip-text text-transparent">
              {name || 'Admin'}
            </span>{' '}
            <span className="inline-block animate-float-slow">👋</span>
          </h1>
          <p className="text-primary-100/90 text-sm mt-2.5 leading-relaxed">{subtitle}</p>
        </div>

        {/* Live metrics row */}
        <div className="flex gap-3 flex-wrap">
          <LiveMetric label="Active Now" target={stats?.activeSessions} pulseColor="bg-emerald-400" />
          <LiveMetric label="Candidates" target={stats?.totalCandidates} pulseColor="bg-sky-400" />
          <LiveMetric label="Pass Rate" target={passRate} suffix="%" pulseColor="bg-amber-400" />
        </div>
      </div>

      {/* Bottom vignette */}
      <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-primary-950/30 to-transparent pointer-events-none" />
    </div>
  );
};

export default DashboardHero;
